import React, { useEffect, useContext, useState, useCallback } from 'react'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { useTranslation } from 'react-i18next'
import { CheckinTabContainer } from '@/components/organisms/settings/checkin-tab-container'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { EditFooter } from '@/components/organisms/edit-footer'
import { css } from '@emotion/core'
import { TextareaField } from '@/components/molecules/textarea-field'
import { Controller, useForm } from 'react-hook-form'
import {
  fetchAdminTabletCheckoutSettings,
  putTabletCheckoutSearchSettingSortOrder,
  putTabletCheckoutSearchSettingIsEnabled,
  updateCheckoutMessages,
  updateSurveyQrcodeEnabled,
  updateTabletCheckoutEnabledAPI,
} from '@/apis/aipass'
import { ToggleButton } from '@/components/molecules/settings/toggle-button'
import { SearchSetting } from '@/models/search-setting'
import { Message } from '@/models/self-checkout/setting'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { Hint } from '@/components/atoms/hint'
import { setHasPrinterPlugin } from '@/libs/plugins'
import { AccountContext } from '@/contexts/account'
import { useSearchItemsManagement } from '@/hooks/use-search-items-management'
import { SearchItemsSection } from '@/components/organisms/settings/search-items-section'

export const SelfCheckout: React.FC = () => {
  const { plugins } = useContext<any>(AccountContext)
  const hasPrinterPlugin = setHasPrinterPlugin(plugins)
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const { t } = useTranslation()

  // メッセージを管理するフォーム
  const messageForm = useForm<Message>({
    mode: 'all',
  })

  const {
    formState: { isValid, isDirty },
  } = messageForm

  // チェックアウトとアンケートQRコード印刷のトグル状態を管理
  const [tabletCheckoutEnabled, setTabletCheckoutEnabled] = useState(false)
  const [surveyQrcodeEnabled, setSurveyQrcodeEnabled] = useState(false)

  // 検索項目の共通Hook
  const {
    formSearchItems,
    defaultSearchItems,
    setDefaultSearchItems,
    reset: resetSearchItems,
    isOrderChanged,
    setIsOrderChanged,
    onCancel: onCancelSearchItems,
    dragStart,
    dragOver,
    dragDrop,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useSearchItemsManagement({
    initialSearchItems: [],
  })

  // searchItem のトグル時の動作
  const toggleSearchItem = useCallback(
    async (i: number, searchItem: SearchSetting) => {
      setIsLoading(true)
      const newIsEnabled = !searchItem.isEnabled
      try {
        await putTabletCheckoutSearchSettingIsEnabled({
          inputType: searchItem.inputType,
          isEnabled: newIsEnabled,
        })
        formSearchItems.update(i, { ...searchItem, isEnabled: newIsEnabled })
        setDefaultSearchItems(
          defaultSearchItems.map(item => (item.inputType === searchItem.inputType ? { ...item, isEnabled: newIsEnabled } : item)),
        )
      } catch (e) {
        errorHandler(e)
      } finally {
        setIsLoading(false)
      }
    },
    [formSearchItems, defaultSearchItems, setIsLoading, errorHandler],
  )

  // キャンセルボタン押下時の処理
  const onCancel = () => {
    // 検索項目の順番を元に戻す
    onCancelSearchItems()
    // メッセージを元に戻す
    messageForm.reset()
  }

  // 保存ボタン押下時の処理
  // 検索項目順序変更とメッセージ変更保存
  const onSave = useCallback(async () => {
    try {
      setIsLoading(true)
      const promises: (Promise<void> | null)[] = [
        isOrderChanged
          ? putTabletCheckoutSearchSettingSortOrder({ searchItems: formSearchItems.fields }).then(() => {
              // 検索項目の順番を更新
              setDefaultSearchItems(formSearchItems.fields)
              // 検索項目の変更フラグをリセット->保存ボタンを隠す
              setIsOrderChanged(false)
            })
          : null,
        isDirty
          ? updateCheckoutMessages({
              complete: messageForm.getValues('complete'),
              keyReturn: messageForm.getValues('keyReturn'),
              frontSupport: messageForm.getValues('frontSupport'),
            }).then(() => {
              messageForm.reset(messageForm.getValues()) // isDirtyをリセット
            })
          : null,
      ]

      await Promise.all(promises.filter(p => p !== null) as Promise<void>[])
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }, [isOrderChanged, isDirty, formSearchItems.fields, messageForm, setDefaultSearchItems, setIsOrderChanged, setIsLoading, errorHandler])

  // 検索項目トグル変更時の処理（即座にAPI呼び出し）
  const updateTabletCheckoutToggle = useCallback(
    async (enabled: boolean) => {
      try {
        setIsLoading(true)
        await updateTabletCheckoutEnabledAPI({ tabletCheckoutEnabled: enabled })
        setTabletCheckoutEnabled(enabled)
      } catch (e) {
        errorHandler(e)
        // エラー時は状態を戻す（オプション）
      } finally {
        setIsLoading(false)
      }
    },
    [setIsLoading, errorHandler],
  )

  const updateSurveyQrcodeToggle = useCallback(
    async (enabled: boolean) => {
      try {
        setIsLoading(true)
        await updateSurveyQrcodeEnabled({ surveyQrcodeEnabled: enabled })
        setSurveyQrcodeEnabled(enabled)
      } catch (e) {
        errorHandler(e)
      } finally {
        setIsLoading(false)
      }
    },
    [setIsLoading, errorHandler],
  )

  useEffect(() => {
    const fetchSetting = async () => {
      try {
        setIsLoading(true)
        const res = await fetchAdminTabletCheckoutSettings()

        // フォーム復元用のstateにset（キャンセルボタン用）
        setDefaultSearchItems(res.searchItems)
        resetSearchItems({ searchItems: res.searchItems })
        // トグルの初期化
        setTabletCheckoutEnabled(res.tabletCheckoutEnabled)
        setSurveyQrcodeEnabled(res.surveyQrcodeEnabled)

        // メッセージフォームの初期化
        messageForm.reset({
          complete: res.message.complete,
          keyReturn: res.message.keyReturn,
          frontSupport: res.message.frontSupport,
        })
      } catch (e) {
        errorHandler(e)
      } finally {
        setIsLoading(false)
      }
    }
    fetchSetting()
  }, [])

  // 保存ボタン
  const footerContent =
    // 検索項目の順番が変更されているか、メッセージが変更されている場合は保存ボタンを表示
    isOrderChanged || isDirty ? <EditFooter onSave={onSave} onCancel={onCancel} isDisabledSave={!isValid} /> : undefined

  return (
    <SettingsLayout loading={false} footerContent={footerContent}>
      <TabContainer tabComponent={<CheckinTabContainer currentTab="Tablet Checkout" />}>
        <div css={componentStyle}>
          <div className="form-block">
            <div>
              <div className="toggle-block">
                <div className="toggle-label">{t('Show Check-out Button')}</div>
                <ToggleButton
                  value={tabletCheckoutEnabled}
                  onChange={async () => {
                    await updateTabletCheckoutToggle(!tabletCheckoutEnabled)
                  }}
                />
              </div>
            </div>
          </div>

          {/* 検索項目セクション */}
          <SearchItemsSection
            searchItems={formSearchItems.fields}
            onToggle={toggleSearchItem}
            onDragStart={dragStart}
            onDragOver={dragOver}
            onDrop={dragDrop}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            containerStyle={css({ marginTop: 0 })}
          />
          {/* メッセージ */}
          <div className="form-block">
            <div className="header-block">
              <div className="title">{t('Completion screen messages')}</div>
              <Hint
                customCss={css({
                  img: { width: 18 },
                  '.help-popup': {
                    color: '#676767',
                    fontSize: 12,
                    left: 23,
                    top: ((50 - 18) / 2) * -1,
                    whiteSpace: 'nowrap',
                    padding: 16,
                    lineHeight: 1.5,
                    fontWeight: 'bold',
                  },
                })}
              >
                {t('Please set the message to display upon checkout completion.')}
              </Hint>
            </div>
            <div>
              {/* メッセージ */}
              <Controller
                control={messageForm.control}
                rules={{ maxLength: 100 }}
                name="complete"
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                  <div className="text-block">
                    <div className="message-title">{t('Thank you')}</div>
                    <div className="input-title">{t('スマートキー利用（客室の鍵番号有）施設で、精算済みのゲストへのメッセージです。')}</div>
                    <TextareaField name={name} value={value} handleChangeData={onChange} marginBottom={0} placeholder="" autoSize={true} />
                    <div className="input-valid-message" style={{ color: error ? 'rgba(255,0,0,0.5)' : 'inherit' }}>
                      {(value || '').length}/100{t('characters')}
                    </div>
                  </div>
                )}
              />
              <Controller
                control={messageForm.control}
                rules={{ maxLength: 100 }}
                name="keyReturn"
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                  <div className="text-block">
                    <div className="message-title">{t('Please return your room key to the front desk.')}</div>
                    <div className="input-title">{t('カードキーやシリンダキー利用施設で、精算済みゲストへのメッセージです。')}</div>
                    <TextareaField name={name} value={value} handleChangeData={onChange} marginBottom={0} placeholder="" autoSize={true} />
                    <div className="input-valid-message" style={{ color: error ? 'rgba(255,0,0,0.5)' : 'inherit' }}>
                      {(value || '').length}/100{t('characters')}
                    </div>
                  </div>
                )}
              />
              <Controller
                control={messageForm.control}
                rules={{ maxLength: 100 }}
                name="frontSupport"
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                  <div className="text-block">
                    <div className="message-title">{t('Please come to the front desk.')}</div>
                    <div className="input-title">{t('鍵の種類を問わず、未精算のあるゲストへのメッセージです。')}</div>
                    <TextareaField name={name} value={value} handleChangeData={onChange} marginBottom={0} placeholder="" autoSize={true} />
                    <div className="input-valid-message" style={{ color: error ? 'rgba(255,0,0,0.5)' : 'inherit' }}>
                      {(value || '').length}/100{t('characters')}
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
          {/* アンケート QR Code 印刷設定 */}
          {hasPrinterPlugin && (
            <div className="form-block survey-qrcode-block">
              <div className="header-block">
                <div className="title">{t('Survey QR code printing setting')}</div>
                <Hint
                  customCss={css({
                    img: { width: 18 },
                    '.help-popup': {
                      color: '#676767',
                      fontSize: 12,
                      left: 23,
                      top: ((50 - 18) / 2) * -1,
                      whiteSpace: 'nowrap',
                      padding: 16,
                      lineHeight: 1.5,
                      fontWeight: 'bold',
                    },
                  })}
                >
                  {t('Please turn on this setting to print a survey QR code upon checkout completion.')}
                </Hint>
              </div>
              <div>
                <div className="toggle-block">
                  <div className="toggle-label">{t('Print QR code')}</div>
                  <ToggleButton
                    label={{ on: t('enable'), off: t('disable') }}
                    value={surveyQrcodeEnabled}
                    onChange={async () => {
                      await updateSurveyQrcodeToggle(!surveyQrcodeEnabled)
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </TabContainer>
    </SettingsLayout>
  )
}

const componentStyle = css({
  marginBottom: 24,
  '.form-block': {
    '.header-block': {
      padding: '0 32px',
      backgroundColor: 'rgb(245, 245, 245)',
      height: 32,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      '.title': { fontWeight: 'bold', fontSize: 12, color: 'rgb(103, 103, 103)' },
    },
    '.text-block': {
      padding: '0 32px',
      borderTop: '1px solid rgb(242, 242, 242)',
      marginTop: 16,
      ':first-of-type': { borderTop: 'none', marginTop: 0 },
      '.message-title': {
        display: 'flex',
        alignItems: 'center',
        fontWeight: 'bold',
        fontSize: 16,
        padding: '16px 0',
      },
      '.input-title': {
        fontSize: 12,
        fontWeight: 'bold',
        paddingBottom: 12,
      },
      '.input-valid-message': { fontSize: 12, paddingTop: 6 },
    },

    '.toggle-block': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderTop: '1px solid rgb(242, 242, 242)',
      ':first-of-type': { borderTop: 'none' },
      height: 62,
      paddingRight: 32,
      paddingLeft: 64,
      '.toggle-label': {
        display: 'flex',
        alignItems: 'center',
        fontWeight: 'bold',
        fontSize: 14,
        p: { fontWeight: 'normal' },
      },
    },
  },
  '.survey-qrcode-block': {
    marginTop: 16,
  },
})
